import React, { useCallback, useRef, useState } from 'react';
import { Skeleton, message } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import { API_URLS } from '../../../constants/urls/api-urls';
import { LoyaltyActionTable } from '../../organisms/misc/LoyaltyActionTable';
import { appStyles } from '../../../styles';
import { MESSAGE_ERROR } from '../../../constants/message';
import { PER_PAGE_OTHER } from '../../../constants/common';
import { LoyaltyActionType } from '../../../types/loyaltyAction';
import { LoyaltyActionPageHeader } from '../../organisms/misc/LoyaltyActionPageHeader';
import { FormLoyaltyActionModal } from '../../organisms/misc/FormLoyaltyActionModal';
import { getLoyaltyListRepository } from '../../../repository/mytm/loyaltyAction/loyaltyActionRepository';
import SelectButtons from '../../atoms/select-buttons/SelectButtons';
import { useCountrySelection } from '../../../hooks/useCountrySelection';

export const LoyaltyAction = () => {
  const [pageActive, setPageActive] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [countrySelected, setCountrySelected] = useCountrySelection(null);
  const [record, setDataRecord] = useState<LoyaltyActionType>();
  const errorCount = useRef(0);

  const { data, isValidating, error, mutate } = useSWR(
    countrySelected ? [API_URLS.LOYALTY_ACTION_LIST, pageActive, countrySelected] : null,
    (url: string, pageNumber: number) =>
      getLoyaltyListRepository(url, {
        country: countrySelected!,
        page: pageNumber,
        take: PER_PAGE_OTHER,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const handleChangePageActive = useCallback(value => {
    setPageActive(value);
  }, []);

  if (error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  const onCloseModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  // change key promotion
  const recordLoyaltyAction = data?.map((item: any, index: number) => ({
    ...item,
    ...{ index },
  }));

  return (
    <Container>
      <LoyaltyActionPageHeader setIsOpenModal={setIsOpenModal} />
      <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
      {!data && (
        <Container>
          <Skeleton active />
        </Container>
      )}
      {data && (
        <LoyaltyActionTable
          onRefresh={mutate}
          records={recordLoyaltyAction || []}
          loading={loading || isValidating}
          setLoading={setLoading}
          setIsOpenModal={setIsOpenModal}
          setDataRecord={setDataRecord}
          pagination={{
            onChange: handleChangePageActive,
            pageSize: PER_PAGE_OTHER,
            current: pageActive,
            total: data?.total,
          }}
        />
      )}

      {isOpenModal && (
        <FormLoyaltyActionModal
          setDataRecord={setDataRecord}
          record={record}
          onRefresh={mutate}
          isOpenModal={isOpenModal}
          onClose={onCloseModal}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}
  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }

  & .ant-page-header {
    padding-left: 0px;
  }
`;
